import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Link,
  Modal,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import {
  Investigation,
  InvestigationRedriveAttempt,
  Stage,
} from "../../lusca-search-api/generated-src";
import { useReleaseReturnedItems } from "../../hooks/useInvestigations";
import { investigationPath } from "./investigationUtils";

/** The case-wide split of returned messages against the case's rule. */
export interface ReturnSplit {
  returned: number;
  /** Still match the case's rule: the fix did not work for these. */
  same: number;
  /** Came back with an error the case's rule no longer matches. */
  newError: number;
  /** New-error returns another case already owned, by investigation id. */
  elsewhere: Record<string, number>;
  /** New-error returns by error type, largest first. */
  errorTypes: [string, number][];
  released: number;
  /** New-error returns this case still holds (what Release frees). */
  held: number;
  /** Any attempt recorded a classification (returns before the feature have none). */
  classified: boolean;
}

export function returnSplit(
  investigation: Investigation,
  attempts: InvestigationRedriveAttempt[]
): ReturnSplit {
  const summaries = attempts
    .map((a) => a.returnSummary)
    .filter((s): s is NonNullable<typeof s> => !!s);
  const elsewhere: Record<string, number> = {};
  const types: Record<string, number> = {};
  const same = summaries.reduce((n, s) => n + (s.sameError ?? 0), 0);
  const newError = summaries.reduce((n, s) => n + (s.newError ?? 0), 0);
  summaries.forEach((s) => {
    Object.entries(s.elsewhere ?? {}).forEach(([id, n]) => {
      elsewhere[id] = (elsewhere[id] ?? 0) + Number(n);
    });
    Object.entries(s.newErrorTypes ?? {}).forEach(([t, n]) => {
      types[t] = (types[t] ?? 0) + Number(n);
    });
  });
  const elsewhereTotal = Object.values(elsewhere).reduce((a, b) => a + b, 0);
  const released = investigation.returnedReleasedCount ?? 0;
  return {
    returned:
      investigation.returnedCount ??
      attempts.reduce((n, a) => n + (a.returnedCount ?? 0), 0),
    same,
    newError,
    elsewhere,
    errorTypes: Object.entries(types).sort((a, b) => b[1] - a[1]),
    released,
    held: Math.max(0, newError - elsewhereTotal - released),
    classified: summaries.length > 0,
  };
}

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * "N redriven messages came back": how many failed the same way (stay in the case) and how
 * many came back with a new error (no longer this case's). The owner releases the new-error
 * ones still held here; a reconcile then hands each to the case whose rule matches it, or to
 * the backlog.
 */
export const ReturnedErrorSplit: React.FC<{
  stage: Stage;
  investigation: Investigation;
  attempts: InvestigationRedriveAttempt[];
  isOwner: boolean;
  onError: (message: string) => void;
}> = ({ stage, investigation, attempts, isOwner, onError }) => {
  const [confirm, setConfirm] = useState(false);
  const release = useReleaseReturnedItems(stage);
  const split = returnSplit(investigation, attempts);
  if (!split.returned) return null;

  const header = `${plural(
    split.returned,
    "redriven message",
    "redriven messages"
  )} came back to the DLQ`;
  if (!split.classified) {
    return (
      <Alert type="warning" header={header}>
        They are linked to their attempt below and the case was reopened.
      </Alert>
    );
  }

  const types = split.errorTypes
    .slice(0, 3)
    .map(([t]) => t)
    .join(", ");
  const moreTypes =
    split.errorTypes.length > 3 ? ` +${split.errorTypes.length - 3}` : "";
  const doRelease = () =>
    release
      .mutateAsync({
        investigationId: investigation.id,
        expectedVersion: investigation.version,
      })
      .then(() => setConfirm(false))
      .catch((e: Error) => {
        setConfirm(false);
        onError(e.message);
      });

  return (
    <>
      <Alert
        type={split.same > 0 ? "warning" : "info"}
        header={header}
        data-testid="returned-error-split"
        action={
          isOwner && split.held > 0 ? (
            <Button onClick={() => setConfirm(true)}>
              Release {split.held.toLocaleString()}…
            </Button>
          ) : undefined
        }
      >
        <SpaceBetween size="xxs">
          {split.same > 0 && (
            <Box data-testid="returned-same">
              {plural(split.same, "is", "are")} failing the same way. The fix
              did not work for {split.same === 1 ? "it" : "them"}, so{" "}
              {split.same === 1 ? "it stays" : "they stay"} in this case.
            </Box>
          )}
          {split.newError > 0 && (
            <Box data-testid="returned-new-error">
              {plural(split.newError, "came", "came")} back with a new error (
              {types}
              {moreTypes}) and no longer match this case&apos;s rule.
              {split.held > 0 &&
                ` ${split.held.toLocaleString()} ${
                  split.held === 1 ? "is" : "are"
                } still held here.`}
              {Object.entries(split.elsewhere).map(([id, n]) => (
                <span key={id}>
                  {" "}
                  {n.toLocaleString()} already in{" "}
                  <Link href={investigationPath(id, stage)} external>
                    {id}
                  </Link>
                  .
                </span>
              ))}
              {split.released > 0 &&
                ` ${split.released.toLocaleString()} released.`}
            </Box>
          )}
        </SpaceBetween>
      </Alert>
      <Modal
        visible={confirm}
        onDismiss={() => setConfirm(false)}
        header={`Release ${plural(
          split.held,
          "returned message",
          "returned messages"
        )}?`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                loading={release.isLoading}
                onClick={() => {
                  doRelease().catch(console.error);
                }}
              >
                Release
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        They came back with a new error, so they no longer belong to this case.
        Within a few minutes each one goes to the active case whose rule matches
        it, or to the backlog.
        {split.same > 0 &&
          ` The ${split.same.toLocaleString()} failing the same way stay here.`}
      </Modal>
    </>
  );
};
